import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Hammer, Leaf, Infinity as InfinityIcon } from 'lucide-react';
import TridentIcon from '../TridentIcon';
import { useScrollVar } from '../../hooks/useScrollVar';
import { useArtifactSlider } from '../../hooks/useArtifactSlider';
import { ArtifactNavigator } from './ArtifactNavigator';
import { ArtifactArrows } from './ArtifactArrows';
import { EditionToggle } from './EditionToggle';
import { ShareConfig } from './ShareConfig';

const BADGES = [
  { Icon: Hammer, lines: ['Handcrafted', 'to Order'] },
  { Icon: Leaf, lines: ['Ethical', 'Materials'] },
  { Icon: InfinityIcon, lines: ['Lifetime', 'Craftsmanship'] },
];

const Copy = ({ realm, piece, isEx, config, onRequest }) => (
  <div className="ahero-copy" data-testid="artifact-copy">
    <p className="alabel hero-reveal" style={{ '--d': '300ms' }} data-testid="artifact-label">
      {realm.name} · Artifact {piece.number}{isEx ? ' (EX)' : ''}
    </p>
    <h1 className="atitle hero-reveal" style={{ '--d': '420ms' }} data-testid="artifact-title">{piece.name}</h1>
    <p className="atype hero-reveal" style={{ '--d': '520ms' }} data-testid="artifact-type">
      <TridentIcon className="w-4 h-4" />
      <span>{piece.type}{isEx ? ' · EX' : ''}</span>
    </p>
    <div className="hero-reveal" style={{ '--d': '560ms' }}>
      <EditionToggle realm={realm} config={config} compact id="hero-edition" />
    </div>
    <p className="acreed hero-reveal" style={{ '--d': '620ms' }} data-testid="artifact-creed">{piece.creed}</p>
    <p className="apassage hero-reveal" style={{ '--d': '720ms' }} data-testid="artifact-passage">
      {piece.passage.map((line, i) => (
        <React.Fragment key={line}>{i > 0 && <br />}{i > 0 && ' '}{line}</React.Fragment>
      ))}
    </p>
    <div className="abtn-row hero-reveal" style={{ '--d': '840ms' }}>
      <button type="button" className="abtn" onClick={onRequest} data-testid="artifact-request-preview-btn">
        <span>Request Preview</span>
        <ArrowRight className="w-4 h-4" strokeWidth={1.25} />
      </button>
      <ShareConfig realm={realm} piece={piece} config={config} variant="icon" />
    </div>
    <ul className="abadges hero-reveal" style={{ '--d': '960ms' }} data-testid="artifact-badges">
      {BADGES.map(({ Icon, lines }) => (
        <li key={lines[0]} className="abadge">
          <span className="abadge-icon"><Icon className="w-3.5 h-3.5" strokeWidth={1.1} /></span>
          <span>{lines[0]}<br />{lines[1]}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const ArtifactHero = ({ realm, piece, isEx, config, dir = 0, onRequest }) => {
  const ref = useRef(null);
  const sceneRef = useRef(null);
  useScrollVar(ref);
  useArtifactSlider(realm, piece.slug, sceneRef);
  const { backdrop } = realm;
  const sceneVars = { '--altx-m': backdrop.altxMobile, '--altx-d': backdrop.altx, '--alty': backdrop.alty };

  return (
    <section ref={ref} className={`ahero ${isEx ? 'is-ex' : ''}`} style={{ '--dir': dir }} data-testid="artifact-hero">
      <Link to={realm.path} className="aback" data-testid="artifact-back-link">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.25} /> Back to {realm.name}
      </Link>

      <div ref={sceneRef} className="ahero-scene" style={sceneVars}>
        <picture>
          <source media="(max-width: 1023px)" srcSet={backdrop.mobile} />
          <img src={backdrop.wide} alt="" className="ahero-bg" draggable="false" />
        </picture>
        <div className="hero-stars hero-stars--a rhero-motes ahero-motes" aria-hidden />
        <div className="rhero-shafts ahero-shafts" aria-hidden><span /><span /><span /></div>
        <div className="ahero-glow" aria-hidden />
        <div className="ahero-veil" aria-hidden />
        <div key={piece.slug} className={`ahero-float ahero-float--${piece.float}`} style={{ '--ratio': piece.ratio }} data-testid="artifact-render">
          <div className="ahero-float-enter">
            <div className="ahero-float-inner">
              <img src={piece.cut} alt={`${piece.name} — ${realm.stones[0].label}`} className="ahero-render ahero-render--base" draggable="false" />
              <img src={piece.cutEx} alt={`${piece.name} — ${realm.stones[1].label}`} className="ahero-render ahero-render--ex" draggable="false" />
            </div>
          </div>
        </div>
        <ArtifactArrows realm={realm} slug={piece.slug} />
      </div>

      <Copy key={piece.slug} realm={realm} piece={piece} isEx={isEx} config={config} onRequest={onRequest} />
      <ArtifactNavigator realm={realm} active={piece.slug} />
    </section>
  );
};
